import { Widget, WidgetHeader, WidgetBody, WidgetFooter } from "../../ui/Widget"
import useStore from "../../../store"
import { useEffect, useState } from "react"
import { NeutralButton, RedButton } from "../../common/Buttons"
import ActionConfirmDrawer from "../../common/ActionConfirmDrawer"
import { DateTime } from "luxon"
import { useNavigate } from "react-router-dom"
import Pagination from "../../common/Pagination"
import { LuChevronDown } from 'react-icons/lu'

export default function FollowUpAudits() {
  const navigate = useNavigate()
  const { followUpAudits, fetchFollowUpAudits, auditsPaginationData, cancelFollowUpAudit } = useStore()
  const [currentPage, setCurrentPage] = useState(1)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [fetchingFollowUpAudits, setFetchingFollowUpAudits] = useState(false)
  const [followUpAuditIdToCancel, setFollowUpAuditIdToCancel] = useState(null)
  const [isCancelingFollowUpAudit, setIsCancelingFollowUpAudit] = useState(false)

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1)
  }

  const auditToCancel = followUpAudits?.find((audit) => audit.id === followUpAuditIdToCancel)
  const cancelAuditHasWork = auditToCancel?.attributes?.['audited-units'] > 0

  const cancelConfirmBody = cancelAuditHasWork
    ? 'Work has already been done on this audit. Cancelling will erase that progress. Are you sure you want to cancel this follow up audit?'
    : 'Are you sure you want to cancel this follow up audit?'

  const handleCancelFollowUpAudit = async () => {
    setIsCancelingFollowUpAudit(true)
    await cancelFollowUpAudit(followUpAuditIdToCancel, () => setFollowUpAuditIdToCancel(null))
    setIsCancelingFollowUpAudit(false)
  }

  useEffect(() => {
    setFetchingFollowUpAudits(true)
    fetchFollowUpAudits(currentPage, () => {
      setFetchingFollowUpAudits(false)
    })
  }, [currentPage])

  return (
    <>
      {followUpAudits?.length > 0 && (
        <Widget>
          <WidgetHeader
            title="Follow Up Audits"
            showSpinner={fetchingFollowUpAudits}
            actionIcon={<LuChevronDown
              size={20}
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`${isCollapsed ? '-rotate-90' : ''} hover:cursor-pointer transition-transform duration-300`}
            />}
          />
          <WidgetBody
            isVisible={!isCollapsed}
          >
            {followUpAudits.map((followUpAudit) => (
              <div
                className='flex w-full items-center border-b-2 border-grey-700
                  p-2 text-grey-300 text-left lg:hover:bg-grey-700 hover:cursor-pointer'
              >
                <div
                  className="flex flex-col w-1/2 md:w-2/3"
                >
                  <div className="font-bold text-sm text-grey-400">
                    {followUpAudit.attributes.dealer.lotname}
                  </div>
                  <div className="font-bold text-lg text-grey-100 truncate pr-10">
                    {followUpAudit.attributes["unit-name"]}
                  </div>
                  <div className="text-xs">
                    {`Started ${DateTime.fromISO(followUpAudit.attributes["time-started"]).toLocaleString()}`}
                  </div>
                </div>

                <div className="flex flex-col gap-2 justify-end w-1/2 md:w-1/3">
                  <NeutralButton text="Resume Audit" onClick={() => navigate(`/field_audits/${followUpAudit.id}`)} />
                  <RedButton text="Cancel Audit" onClick={() => setFollowUpAuditIdToCancel(followUpAudit.id)} />
                </div>
              </div>
            ))}
          </WidgetBody>
          {!isCollapsed && (
            <WidgetFooter isSticky>
              <Pagination
                currentPage={auditsPaginationData.currentPage}
                lastPage={auditsPaginationData.lastPage}
                dataTotal={auditsPaginationData.dataTotal}
                handlePageClick={handlePageClick}
                totalPages={auditsPaginationData.totalPages}
              />
            </WidgetFooter>
          )}
        </Widget>
      )}
      <ActionConfirmDrawer
        title="Cancel Follow Up Audit"
        body={cancelConfirmBody}
        onConfirm={handleCancelFollowUpAudit}
        onCancel={() => setFollowUpAuditIdToCancel(null)}
        isOpen={!!followUpAuditIdToCancel}
        onClose={() => setFollowUpAuditIdToCancel(null)}
        isConfirming={isCancelingFollowUpAudit}
      />
    </>
  )
}
